import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe, LangService, RouteWatcher } from '@reforgium/presentia';
import {
  SkAutocompleteComponent,
  SkButtonComponent,
  SkDatePickerComponent,
  SkFormFieldComponent,
  SkInputTextComponent,
  SkMultiSelectComponent,
  SkSelectComponent,
} from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { DocumentLangs, DocumentsFilter, DocumentSortField } from 'entities/document.model';
import { DictService } from 'use-cases/dict.service';
import { DocumentService } from 'use-cases/document.service';
import { SearchAction } from 'features/actions/search-actions';
import { MovingGradientDirective } from 'features/panels/moving-gradient.directive';

type DocumentSortOption = {
  value: DocumentSortField;
  label: string;
};

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter.panel.html',
  imports: [
    IconComponent,
    LangPipe,
    SearchAction,
    MovingGradientDirective,
    SkAutocompleteComponent,
    SkButtonComponent,
    SkDatePickerComponent,
    SkFormFieldComponent,
    SkInputTextComponent,
    SkMultiSelectComponent,
    SkSelectComponent,
    FormsModule,
  ],
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
    .search-panel {
      padding: 16px;
      border-radius: 18px;
      background: #1c3449;
      color: #fff;
    }
    .sidebar-panel {
      padding-bottom: 212px;
      background-image: url('assets/bg/Illustration.png');
      background-size: 230px 190px;
      background-position: center bottom 12px;
      background-repeat: no-repeat;
    }
    .panel-header {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .filter-content {
      display: grid;
      grid-template-rows: 0fr;
      visibility: hidden;
      opacity: 0;
      transition:
        grid-template-rows 200ms,
        opacity 200ms;
    }
    .filter-content-open {
      grid-template-rows: 1fr;
      visibility: visible;
      opacity: 1;
    }
    .filter-content-inner {
      min-height: 0;
      overflow: hidden;
    }
    .filter-content-open .filter-content-inner {
      overflow: visible;
    }
    .type-selection {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 6px;
    }
    .type-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      min-height: 22px;
      padding: 2px 6px 2px 8px;
      border: 1px solid rgb(255 255 255 / 22%);
      border-radius: 999px;
      background: rgb(255 255 255 / 11%);
      color: #fff;
      font-size: calc(11px * var(--a11y-font-scale, 1));
      line-height: 1.2;
    }
    .type-chip button {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      line-height: 0;
      cursor: pointer;
    }
    .type-chip button re-icon {
      display: block;
    }
    .type-chip button:focus-visible {
      outline: 2px solid #2da5fa;
      outline-offset: 1px;
      border-radius: 50%;
    }
    .form-dropdown-box {
      display: grid;
      grid-template-columns: 1fr;
      gap: 12px;
      margin-top: 18px;
    }
    .form-dropdown-box > sk-field:first-child {
      position: relative;
      z-index: 3;
    }
    .form-dropdown-box-full {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    .form-dropdown-box-full > .grid {
      grid-column: span 2;
    }
    .form-dropdown-box:not(.form-dropdown-box-full) > .grid {
      grid-template-columns: 1fr;
    }
    .filter-submit {
      margin-top: 12px;
    }
    .full-panel .filter-submit {
      margin-top: -34px;
    }
    @media (max-width: 900px) {
      .form-dropdown-box-full {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .full-panel .filter-submit {
        margin-top: 12px;
      }
    }
    @media (max-width: 700px) {
      .sidebar-panel {
        background-image: none;
        padding-bottom: 16px;
      }
      .form-dropdown-box-full {
        grid-template-columns: 1fr;
      }
      .form-dropdown-box-full > .grid {
        grid-column: auto;
      }
    }
  `,
  providers: [DocumentService],
})
export class FilterPanel {
  isFullWidth = input(false, {
    transform: booleanAttribute,
  });
  isDocumentsLoading = input<boolean>(false);
  showSorting = input(false, { transform: booleanAttribute });

  private router = inject(Router);
  private watcher = inject(RouteWatcher);
  private langService = inject(LangService);

  protected dictService = inject(DictService);

  protected readonly searchFormControl = new FormControl('', { nonNullable: true });
  protected panelCollapsed = signal(true);
  protected types = signal<string[]>([]);
  protected typeCandidate = signal<string | null>(null);
  protected typeAutocomplete = viewChild<SkAutocompleteComponent>('typeAutocomplete');
  protected availableTypeOptions = computed(() => {
    this.langService.currentLang();

    return this.dictService.types.options().filter((option) => !this.types().includes(option.value));
  });
  protected documentNumber = signal('');
  protected content = signal('');
  protected statuses = signal<string[]>([]);
  protected dateFrom = signal<Date | null>(null);
  protected dateTo = signal<Date | null>(null);
  protected lang = signal<DocumentLangs | null>(null);
  protected sortBy = signal<DocumentSortField>('updatedAt');
  protected readonly sortOptions: DocumentSortOption[] = [
    { value: 'updatedAt', label: 'common.filter.sort-updated-at' },
    { value: 'createDate', label: 'common.filter.sort-created-at' },
  ];
  constructor() {
    effect(() => {
      const typedParams = this.watcher.query() as DocumentsFilter;

      untracked(() => {
        this.searchFormControl.setValue(typedParams.title ?? '');
        this.types.set(this.toArray(typedParams.types));
        this.typeCandidate.set(null);
        this.documentNumber.set(typedParams.documentNumber ?? '');
        this.content.set(typedParams.content ?? '');
        this.statuses.set(this.toArray(typedParams.statuses));
        this.lang.set(typedParams.lang ?? null);
        this.dateFrom.set(this.parseDate(typedParams.dateFrom));
        this.dateTo.set(this.parseDate(typedParams.dateTo));
        this.sortBy.set(typedParams.sortBy === 'createDate' ? 'createDate' : 'updatedAt');
      });
    });
  }

  protected addType(type: string): void {
    this.types.update((selected) => (selected.includes(type) ? selected : [...selected, type]));
    this.typeCandidate.set(null);
    this.typeAutocomplete()?.value.set(null);
    this.typeAutocomplete()?.query.set('');
  }

  protected removeType(type: string): void {
    this.types.update((selected) => selected.filter((value) => value !== type));
  }

  protected typeLabel(type: string): string {
    return this.dictService.types.options().find((option) => option.value === type)?.label ?? type;
  }

  protected readonly localizedSortLabel = (option: unknown): string => {
    if (!option || typeof option !== 'object' || !('label' in option)) {
      return '';
    }

    return this.langService.get((option as DocumentSortOption).label);
  };

  protected handleSubmit(): void {
    const from = this.dateFrom();
    const to = this.dateTo();

    if (from && to && from > to) {
      return;
    }

    void this.router.navigate([urls.documentsList], {
      queryParams: {
        dateFrom: this.toQueryDate(from),
        dateTo: this.toQueryDate(to),
        title: this.searchFormControl.value || null,
        documentNumber: this.documentNumber() || null,
        content: this.content() || null,
        types: this.types().length ? this.types() : null,
        statuses: this.statuses().length ? this.statuses() : null,
        lang: this.lang(),
        sortBy: this.sortBy(),
      },
    });
  }

  private toArray<T>(value: T | T[] | null | undefined): T[] {
    return value ? (Array.isArray(value) ? value : [value]) : [];
  }

  private parseDate(value: string | null | undefined): Date | null {
    return value ? new Date(value) : null;
  }

  private toQueryDate(value: Date | null): string | null {
    if (!value) {
      return null;
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
